import moment from 'moment';
import ReedsyExitModal from '@reedsy/exit-modal';
import LocalStorage from '@d-shared/utils/local-storage';

import SASS from '@dui/helpers/sass';

const DATE_FORMAT = 'YYYY-MM-DD HH:mm:ss';
const EXIT_MODAL_ID = 'exit-modal';
const TIMEOUT_MS = 250;

export class ExitModalHandler {
  constructor (vue) {
    this._vue = vue;
    this._exitModal = null;
    this._currentScrollTop = null;

    this._scrollingElement = document.scrollingElement;

    this.windowScrollPerformed = this.windowScrollPerformed.bind(this);
    this.initialize = this.initialize.bind(this);
    this.initAndShow = this.initAndShow.bind(this);
    this.callbackTriggered = this.callbackTriggered.bind(this);

    this.setup();
  }

  callbackTriggered() {
    if (this._vue.prototype.trackingHandler &&
      this._vue.prototype.trackingHandler.exitModalShown) {
      this._vue.prototype.trackingHandler.exitModalShown(EXIT_MODAL_ID);
    }

    this._vue.prototype.modalHandler.show(`#${ EXIT_MODAL_ID }`);
    ExitModalHandler.saveShownDate();
    this.deactivateExitModal();
  }

  deactivateExitModal() {
    if (!this._exitModal) {
      return;
    }

    this._exitModal.destroy();
    window.removeEventListener('scroll', this.windowScrollPerformed);
  }

  hideModal() {
    if (!this._exitModal) {
      return;
    }

    this._exitModal.hide();
  }

  initialize(modalId = EXIT_MODAL_ID, auto = true, forced = false) {
    const exitModalElement = document.getElementById(modalId);
    if (!exitModalElement) {
      return;
    }

    if (!forced &&
      !ExitModalHandler.shouldActivate(exitModalElement)) {
      return;
    }

    const modalKey = exitModalElement.dataset.modalKey || null;
    const modal = new ReedsyExitModal(modalId, {
      callback: this.callbackTriggered,
      classes: {
        hidden: 'normal',
        visible: 'active',
      },
      key: modalKey,
    });

    this._exitModal = modal;

    this.initMobileScrollActivation(modal);

    return auto ? true : modal;
  }

  initAndShow(modalId = EXIT_MODAL_ID) {
    const modal = this.initialize(modalId, false, true);
    if (!modal) return;
    modal.show(true);
  }

  initMobileScrollActivation(modal) {
    // Activate on mobile only
    if (window.innerWidth >= SASS.screenSize.md) {
      return;
    }

    if (modal._element.dataset.showAtPosition
      && (modal._element.dataset.showOnScrollDirection === 'up'
        || modal._element.dataset.showOnScrollDirection === 'down')) {
      window.addEventListener('scroll', this.windowScrollPerformed);
    }
  }

  setup() {
    this._currentScrollTop = this._scrollingElement.scrollTop;

    setTimeout(this.initialize, TIMEOUT_MS);
  }

  windowScrollPerformed() {
    if (!this._exitModal) {
      return;
    }

    const newScrollTop = this._scrollingElement.scrollTop;

    // Only fire if the scroll changes more than 10px
    if (Math.abs(newScrollTop - this._currentScrollTop) <= 10) {
      return;
    }

    const showOnScrollPosition = parseInt(this._exitModal._element.dataset.showAtPosition);

    if (newScrollTop < showOnScrollPosition) {
      return;
    }

    const shouldShowModal = (this._exitModal._element.dataset.showOnScrollDirection === 'up' &&
      newScrollTop < this._currentScrollTop) ||
      (this._exitModal._element.dataset.showOnScrollDirection === 'down' &&
        newScrollTop > this._currentScrollTop);

    if (shouldShowModal) {
      this._exitModal.show(true);
      this.deactivateExitModal();
    }

    this._currentScrollTop = newScrollTop;
  }

  static saveShownDate() {
    const exitModalElement = document.getElementById(EXIT_MODAL_ID);
    const modalKey = exitModalElement.dataset.modalKey || EXIT_MODAL_ID;
    LocalStorage.setItem(`${ modalKey }-shown-date`, moment().format(DATE_FORMAT));
  }

  static shouldActivate(modalElement) {
    if (!modalElement.dataset.disablePeriodType ||
      !modalElement.dataset.disablePeriodValue) {
      return true;
    }

    const modalKey = modalElement.dataset.modalKey || EXIT_MODAL_ID;
    const storedShownDate = LocalStorage.getItem(`${ modalKey }-shown-date`);
    if (!storedShownDate) {
      return true;
    }

    try {
      const shownDate = moment(storedShownDate, DATE_FORMAT);
      const timeDifference = moment().diff(shownDate, modalElement.dataset.disablePeriodType);
      const isToActivate = timeDifference >= modalElement.dataset.disablePeriodValue;
      if (isToActivate) {
        LocalStorage.removeItem(`${ modalKey }-shown-date`);
      }

      return isToActivate;
    } catch(e) {
      return true;
    }
  }
}

export default {
  install: function(Vue, options) {
    Vue.prototype.exitModalHandler = new ExitModalHandler(Vue);
  },
};
